import { defaultErrorHandler } from "./errors.js";
import type { ComposeOptions, HandlerContext, HandlerUtility, NextRequest, RouteHandler } from "./types.js";

/**
 * Compose multiple utilities into a single route handler.
 *
 * @example
 * ```ts
 * import { compose, withCors, withAuth, withRateLimit } from "next-route-compose";
 *
 * export const GET = compose(
 *   withCors({ origin: "*" }),
 *   withAuth({ verify: (ctx) => verifyToken(ctx.request) }),
 *   withRateLimit({ limit: 100, window: 60 })
 * )(async (ctx) => {
 *   return Response.json({ user: ctx.user });
 * });
 * ```
 */
export function compose<TContext extends HandlerContext = HandlerContext>(
    ...utilities: HandlerUtility[]
): (handler: RouteHandler<TContext>, options?: ComposeOptions) => (request: NextRequest) => Promise<Response> {
    return (handler: RouteHandler<TContext>, options?: ComposeOptions) => {
        const onError = options?.onError ?? defaultErrorHandler;

        // Compose utilities from right to left
        const composedHandler = utilities.reduceRight(
            (next, utility) => utility(next as RouteHandler),
            handler as RouteHandler
        );

        return async (request: NextRequest): Promise<Response> => {
            const context: HandlerContext = { request };

            try {
                return await composedHandler(context);
            } catch (error) {
                return await onError(error, context);
            }
        };
    };
}

/**
 * Create a handler with a fluent builder API.
 *
 * @example
 * ```ts
 * import { createHandler, withCors, withAuth } from "next-route-compose";
 *
 * export const GET = createHandler()
 *   .use(withCors({ origin: "*" }))
 *   .use(withAuth({ verify: verifyToken }))
 *   .handle(async (ctx) => {
 *     return Response.json({ user: ctx.user });
 *   });
 * ```
 */
export function createHandler() {
    const utilities: HandlerUtility[] = [];

    const builder = {
        /**
         * Add a utility to the handler chain.
         */
        use<TContextIn extends HandlerContext, TContextOut extends HandlerContext>(
            utility: HandlerUtility<TContextIn, TContextOut>
        ) {
            utilities.push(utility as unknown as HandlerUtility);
            return builder;
        },

        /**
         * Set the final handler and return the composed route handler.
         */
        handle<TContext extends HandlerContext>(
            handler: RouteHandler<TContext>,
            options?: ComposeOptions
        ): (request: NextRequest) => Promise<Response> {
            return compose(...utilities)(handler as RouteHandler, options);
        },
    };

    return builder;
}
